import { B, type Blocks } from './blocks';
import type { HabitatType } from '../lib/aquarium';

// ─────────────────────────────────────────────────────────────────────────────
// Habitats: build a space, put a Fish beacon inside, and fish move in. What you
// build it from decides who comes:
//
//   Coral garden     coral + coral bricks ≥ 8          reef fish
//   Sunken hideout   wood + planks ≥ 8                 wreck fish
//   Crystal grotto   crystal + crystal lamps ≥ 6       glowing deep-sea fish
//   Glass tank       glass ≥ 10                        showy fish, jellies
//   Stone fortress   stone + stone/temple bricks +
//                    dark stone ≥ 12                   crabs, squid, rays
//   Sand bed         sand ≥ 10                         perch, crabs, rays, starfish
//   Grand aquarium   3+ of those at half their amount,
//                    40+ blocks, a big inside          the rare and legendary ones
//
// Only blocks the class PLACED count (not the map's own), within BOX cells of
// the beacon — the server counts the same way (ocean_attract in
// supabase/migrations/20261021_big_ocean.sql) and decides who arrives. Fish only
// come while the inside is closed in (checked here, on the screen of whoever is
// visiting); capacity grows with the number of blocks.
// ─────────────────────────────────────────────────────────────────────────────

export const BOX = 8;
/** The most open cells a closed-in habitat can have (bigger = not closed in). */
const MAX_INSIDE = 260;

export const HABITAT_INFO: Record<HabitatType, { name: string; icon: string; blurb: string }> = {
  coral: { name: 'Coral garden', icon: '🪸', blurb: '8+ coral or coral bricks' },
  wreck: { name: 'Sunken hideout', icon: '⚓', blurb: '8+ wood or planks' },
  crystal: { name: 'Crystal grotto', icon: '💎', blurb: '6+ crystal or crystal lamps' },
  glass: { name: 'Glass tank', icon: '🫙', blurb: '10+ glass' },
  stone: { name: 'Stone fortress', icon: '🏰', blurb: '12+ stone, bricks or dark stone' },
  sand: { name: 'Sand bed', icon: '🏖️', blurb: '10+ sand' },
  grand: { name: 'Grand aquarium', icon: '🌈', blurb: '3+ of the others at half, 40+ blocks in all' },
};

/** Block ids that count toward each type, and how many it needs. */
const RULES: [Exclude<HabitatType, 'grand'>, number[], number][] = [
  ['coral', [3, 9], 8],
  ['wreck', [5, 10], 8],
  ['crystal', [4, 11], 6],
  ['glass', [7], 10],
  ['stone', [2, 6, 8, 12], 12],
  ['sand', [1], 10],
];

export type HabitatStatus = {
  type: HabitatType | null;
  /** Placed blocks around it, all kinds. */
  total: number;
  enclosed: boolean;
  /** Open cells inside (when closed in): where its fish swim. */
  inside: [number, number][];
  capacity: number;
  /** What each type has, toward what it needs (for the hint). */
  progress: { type: Exclude<HabitatType, 'grand'>; have: number; need: number }[];
};

/** Which habitat type these placed-block counts make (the same rule as the server's). */
export function habitatType(counts: Map<number, number>): { type: HabitatType | null; total: number; progress: HabitatStatus['progress'] } {
  let total = 0;
  for (const [id, n] of counts) if (id !== 13) total += n;
  const progress = RULES.map(([type, ids, need]) => ({ type, have: ids.reduce((a, id) => a + (counts.get(id) ?? 0), 0), need }));
  const halfway = progress.filter(p => p.have * 2 >= p.need).length;
  if (halfway >= 3 && total >= 40) return { type: 'grand', total, progress };
  let best: (typeof progress)[number] | null = null;
  for (const p of progress) if (p.have >= p.need && (!best || p.have / p.need > best.have / best.need)) best = p;
  return { type: best?.type ?? null, total, progress };
}

export const capacityFor = (total: number) => Math.min(8, 1 + Math.floor(total / 8));

/** Look at the habitat around a beacon at (tx, ty). */
export function habitatAt(blocks: Blocks, tx: number, ty: number): HabitatStatus {
  const counts = new Map<number, number>();
  for (let y = ty - BOX; y <= ty + BOX; y++) for (let x = tx - BOX; x <= tx + BOX; x++) {
    if (!blocks.inside(x, y)) continue;
    const t = blocks.placed.get(blocks.idx(x, y));
    if (t) counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  const { type, total, progress } = habitatType(counts);

  // Closed in? Fill the open water around the beacon; it must stay small and not leak out of the box.
  const seen = new Set<number>();
  const inside: [number, number][] = [];
  const q: [number, number][] = [];
  for (const [dx, dy] of [[0, -1], [-1, 0], [1, 0], [0, 1]]) if (!blocks.solid(tx + dx, ty + dy)) q.push([tx + dx, ty + dy]);
  let enclosed = q.length > 0;
  while (q.length && enclosed) {
    const [x, y] = q.pop()!;
    const i = blocks.idx(x, y);
    if (seen.has(i)) continue;
    seen.add(i);
    if (Math.abs(x - tx) > BOX + 4 || Math.abs(y - ty) > BOX + 4 || seen.size > MAX_INSIDE) { enclosed = false; break; }
    inside.push([x, y]);
    for (const [dx, dy] of [[0, -1], [-1, 0], [1, 0], [0, 1]]) if (!blocks.solid(x + dx, y + dy) && !seen.has(blocks.idx(x + dx, y + dy))) q.push([x + dx, y + dy]);
  }
  return { type, total, enclosed, inside: enclosed ? inside : [], capacity: capacityFor(total), progress };
}

/** The middle of a habitat's inside, in world units (for labels). */
export function insideCentre(h: HabitatStatus, tx: number, ty: number): { x: number; y: number } {
  if (!h.inside.length) return { x: tx * B + B / 2, y: ty * B };
  let sx = 0, sy = 0;
  for (const [x, y] of h.inside) { sx += x; sy += y; }
  return { x: (sx / h.inside.length) * B + B / 2, y: (sy / h.inside.length) * B + B / 2 };
}
